/**
 * DemandJourneyWizard - Sprint 10 Phase 2
 * Guided journey experience for demand management
 * Visual progress indicators and section navigation
 */

import React from 'react';
import { CheckCircle, Circle, ArrowRight, AlertTriangle, Clock, Sparkles } from 'lucide-react';

// Journey stages configuration
export const JOURNEY_STAGES = [
  { 
    key: 'intake', 
    label: 'Intake', 
    description: 'Initial submission and basic details',
    requiredFields: ['title', 'description', 'department'],
    color: 'blue'
  },
  { 
    key: 'assessment', 
    label: 'Assessment', 
    description: 'Business analysis and scoring',
    requiredFields: ['impact', 'urgency', 'business_value', 'strategic_alignment'],
    color: 'yellow'
  },
  { 
    key: 'governance', 
    label: 'Governance', 
    description: 'Review and approval decision',
    requiredFields: ['complexity', 'cost_estimate'],
    color: 'purple'
  },
  { 
    key: 'outcome', 
    label: 'Outcome', 
    description: 'Approved, rejected, or converted',
    requiredFields: [],
    color: 'green'
  }
];

// Section navigation configuration
export const DEMAND_SECTIONS = [
  { key: 'overview', label: 'Overview', icon: 'FileText', stage: 'intake' },
  { key: 'problem', label: 'Problem Statement', icon: 'AlertTriangle', stage: 'intake' },
  { key: 'benefits', label: 'Benefits', icon: 'TrendingUp', stage: 'assessment' },
  { key: 'requirements', label: 'Requirements', icon: 'ClipboardList', stage: 'assessment' },
  { key: 'risks', label: 'Risks & Dependencies', icon: 'Shield', stage: 'governance' },
  { key: 'attachments', label: 'Attachments', icon: 'Paperclip', stage: 'intake' },
  { key: 'scoring', label: 'Scoring', icon: 'Target', stage: 'assessment' },
  { key: 'approval', label: 'Approval', icon: 'CheckCircle', stage: 'governance' }
];

/**
 * Get the current stage index based on status
 */
export const getStageIndex = (status) => {
  const statusToStage = {
    'intake': 0,
    'external_intake': 0,
    'assessment': 1,
    'governance': 2,
    'approved': 3,
    'rejected': 3
  };
  return statusToStage[status] ?? 0;
};

/**
 * Check if a stage is complete based on demand data
 */
export const isStageComplete = (stage, demand) => {
  if (!demand) return false;
  
  const attrs = demand.attributes || demand;
  
  return stage.requiredFields.every(field => {
    const value = attrs[field];
    if (typeof value === 'number') return value > 0;
    if (typeof value === 'string') return value.length > 0;
    return !!value;
  });
};

/**
 * Get missing fields for a stage
 */
export const getMissingFields = (stage, demand) => {
  if (!demand) return stage.requiredFields;
  
  const attrs = demand.attributes || demand;
  
  return stage.requiredFields.filter(field => {
    const value = attrs[field];
    if (typeof value === 'number') return value <= 0;
    if (typeof value === 'string') return value.length === 0;
    return !value;
  });
};

/**
 * Progress Stepper Component
 */
export const JourneyProgressStepper = ({ currentStatus, demand }) => {
  const currentIndex = getStageIndex(currentStatus);
  const isTerminal = currentStatus === 'approved' || currentStatus === 'rejected';
  
  return (
    <div className="bg-white border rounded-xl p-6 mb-6" data-testid="journey-stepper">
      <div className="flex items-center justify-between">
        {JOURNEY_STAGES.map((stage, index) => {
          const isActive = index === currentIndex;
          const isCompleted = index < currentIndex;
          const isPending = index > currentIndex;
          const stageComplete = isStageComplete(stage, demand);
          
          // Determine colors
          let bgColor = 'bg-gray-100';
          let textColor = 'text-gray-400';
          let borderColor = 'border-gray-200';
          
          if (isCompleted) {
            bgColor = 'bg-green-100';
            textColor = 'text-green-600';
            borderColor = 'border-green-300';
          } else if (isActive) {
            if (currentStatus === 'rejected') {
              bgColor = 'bg-red-100';
              textColor = 'text-red-600';
              borderColor = 'border-red-300';
            } else if (currentStatus === 'approved') {
              bgColor = 'bg-green-100';
              textColor = 'text-green-600';
              borderColor = 'border-green-300';
            } else {
              bgColor = 'bg-indigo-100';
              textColor = 'text-indigo-600';
              borderColor = 'border-indigo-300';
            }
          }
          
          return (
            <React.Fragment key={stage.key}>
              <div className="flex flex-col items-center flex-1">
                <div className={`relative w-12 h-12 rounded-full ${bgColor} border-2 ${borderColor} flex items-center justify-center transition-all`}>
                  {isCompleted ? (
                    <CheckCircle className={`h-6 w-6 ${textColor}`} />
                  ) : isActive && isTerminal ? (
                    currentStatus === 'approved' ? (
                      <CheckCircle className={`h-6 w-6 ${textColor}`} />
                    ) : (
                      <AlertTriangle className={`h-6 w-6 ${textColor}`} />
                    )
                  ) : (
                    <span className={`text-lg font-semibold ${textColor}`}>{index + 1}</span>
                  )}
                  
                  {/* Stage completion indicator */}
                  {isActive && !isTerminal && !stageComplete && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full border-2 border-white flex items-center justify-center">
                      <AlertTriangle className="h-2.5 w-2.5 text-yellow-800" />
                    </div>
                  )}
                </div>
                
                <div className="mt-2 text-center">
                  <p className={`text-sm font-medium ${isActive || isCompleted ? 'text-gray-900' : 'text-gray-400'}`}>
                    {stage.label}
                  </p>
                  {isActive && !isTerminal && (
                    <p className="text-xs text-indigo-600 mt-0.5">You are here</p>
                  )}
                  {isActive && currentStatus === 'approved' && (
                    <p className="text-xs text-green-600 mt-0.5">Approved</p>
                  )}
                  {isActive && currentStatus === 'rejected' && (
                    <p className="text-xs text-red-600 mt-0.5">Rejected</p>
                  )}
                </div>
              </div>
              
              {/* Connector */}
              {index < JOURNEY_STAGES.length - 1 && (
                <div className={`flex-shrink-0 w-16 h-1 mx-2 rounded ${isCompleted ? 'bg-green-300' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Next Step Required Guidance Component
 */
export const NextStepGuidance = ({ currentStatus, demand, onNavigateToSection }) => {
  if (currentStatus === 'approved' || currentStatus === 'rejected') {
    return null;
  }
  
  const currentIndex = getStageIndex(currentStatus);
  const currentStage = JOURNEY_STAGES[currentIndex];
  const missingFields = getMissingFields(currentStage, demand);
  const stageComplete = missingFields.length === 0;
  
  const nextStage = JOURNEY_STAGES[currentIndex + 1];
  const nextStatuses = {
    'intake': 'assessment',
    'assessment': 'governance',
    'governance': 'approved'
  };
  const nextStatus = nextStatuses[currentStatus];
  
  // Field to section mapping
  const fieldToSection = {
    'title': 'overview',
    'description': 'problem',
    'department': 'overview',
    'impact': 'scoring',
    'urgency': 'scoring',
    'business_value': 'scoring',
    'strategic_alignment': 'scoring',
    'complexity': 'scoring',
    'cost_estimate': 'scoring'
  };
  
  // Friendly field names
  const fieldLabels = {
    'title': 'Title',
    'description': 'Description',
    'department': 'Department',
    'impact': 'Impact Level',
    'urgency': 'Urgency Level',
    'business_value': 'Business Value Score',
    'strategic_alignment': 'Strategic Alignment Score',
    'complexity': 'Complexity Score',
    'cost_estimate': 'Cost Estimate'
  };
  
  return (
    <div className={`rounded-xl p-4 mb-6 border-2 ${stageComplete ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}
         data-testid="next-step-guidance">
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${stageComplete ? 'bg-green-100' : 'bg-amber-100'}`}>
          {stageComplete ? (
            <CheckCircle className="h-5 w-5 text-green-600" />
          ) : (
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          )}
        </div>
        
        <div className="flex-1">
          {stageComplete ? (
            <>
              <h4 className="font-medium text-green-800">Ready for {nextStage?.label || 'Next Stage'}</h4>
              <p className="text-sm text-green-700 mt-1">
                All required information for the {currentStage.label} stage is complete. 
                {nextStatus && ` You can now move this demand to ${nextStatus}.`}
              </p>
            </>
          ) : (
            <>
              <h4 className="font-medium text-amber-800">Complete {currentStage.label} Stage</h4>
              <p className="text-sm text-amber-700 mt-1">
                The following fields are required before moving to {nextStage?.label || 'the next stage'}:
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {missingFields.map(field => (
                  <button
                    key={field}
                    onClick={() => onNavigateToSection?.(fieldToSection[field] || 'overview')}
                    className="px-3 py-1 bg-white border border-amber-300 rounded-full text-xs font-medium text-amber-800 hover:bg-amber-100 transition-colors"
                  >
                    {fieldLabels[field] || field}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        
        {stageComplete && (
          <div className="flex items-center gap-1 text-green-600">
            <ArrowRight className="h-4 w-4" />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Section Navigation Component
 */
export const SectionNavigation = ({ 
  activeSection, 
  onSectionChange, 
  demand,
  showAIActions = true,
  onAIAction
}) => {
  const attrs = demand?.attributes || demand || {};
  
  // Check if a section has content
  const hasContent = (section) => {
    switch (section.key) {
      case 'overview': return !!attrs.title;
      case 'problem': return !!attrs.description;
      case 'benefits': return !!attrs.business_outcomes;
      case 'requirements': return !!attrs.requirements;
      case 'risks': return !!attrs.risks;
      case 'attachments': return (attrs.attachments?.length || 0) > 0;
      case 'scoring': return (attrs.impact || attrs.urgency || attrs.business_value);
      case 'approval': return attrs.status === 'approved' || attrs.status === 'rejected';
      default: return false;
    }
  };
  
  return (
    <div className="bg-white border rounded-xl p-4" data-testid="section-nav">
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
        Sections
      </h3>
      <nav className="space-y-1">
        {DEMAND_SECTIONS.map(section => {
          const isActive = activeSection === section.key;
          const hasData = hasContent(section);
          
          return (
            <button
              key={section.key}
              onClick={() => onSectionChange(section.key)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                isActive 
                  ? 'bg-indigo-50 text-indigo-700 font-medium' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              data-testid={`nav-${section.key}`}
            >
              <span className="flex items-center gap-2">
                <span>{section.label}</span>
              </span>
              
              <span className="flex items-center gap-1">
                {hasData && (
                  <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                )}
                {showAIActions && section.key !== 'attachments' && section.key !== 'approval' && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAIAction?.(section.key);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        onAIAction?.(section.key);
                      }
                    }}
                    className="p-1 hover:bg-indigo-100 rounded opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    title="AI Assist"
                  >
                    <Sparkles className="h-3.5 w-3.5 text-indigo-400" />
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </nav>
      
      {/* AI Quick Actions */}
      {showAIActions && (
        <div className="mt-4 pt-4 border-t">
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
            AI Quick Actions
          </h4>
          <div className="space-y-1">
            <button
              onClick={() => onAIAction?.('analyze')}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            >
              <Sparkles className="h-4 w-4" />
              Analyze Demand
            </button>
            <button
              onClick={() => onAIAction?.('suggest_next')}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            >
              <ArrowRight className="h-4 w-4" />
              Suggest Next Steps
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Time in Stage Indicator
 */
export const TimeInStageIndicator = ({ demand, history }) => {
  if (!demand) return null;
  
  const attrs = demand.attributes || demand;
  const currentStatus = attrs.status;
  
  // Find when we entered current status
  const currentTransition = history?.find(h => h.to_status === currentStatus);
  
  let daysInStage = 0;
  if (currentTransition?.transitioned_at) {
    const enteredAt = new Date(currentTransition.transitioned_at);
    const now = new Date();
    daysInStage = Math.floor((now - enteredAt) / (1000 * 60 * 60 * 24));
  } else if (attrs.created_at) {
    const createdAt = new Date(attrs.created_at);
    const now = new Date();
    daysInStage = Math.floor((now - createdAt) / (1000 * 60 * 60 * 24));
  }
  
  // SLA thresholds (days)
  const slaThresholds = {
    'intake': 5,
    'assessment': 10,
    'governance': 7
  };
  
  const threshold = slaThresholds[currentStatus] || 14;
  const isOverdue = daysInStage > threshold;
  const isWarning = daysInStage > threshold * 0.7;
  
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
      isOverdue ? 'bg-red-100 text-red-700' :
      isWarning ? 'bg-amber-100 text-amber-700' :
      'bg-gray-100 text-gray-600'
    }`}>
      <Clock className="h-3.5 w-3.5" />
      <span>{daysInStage} day{daysInStage !== 1 ? 's' : ''} in {currentStatus}</span>
      {isOverdue && <AlertTriangle className="h-3.5 w-3.5" />}
    </div>
  );
};

export default {
  JOURNEY_STAGES,
  DEMAND_SECTIONS,
  getStageIndex,
  isStageComplete,
  getMissingFields,
  JourneyProgressStepper,
  NextStepGuidance,
  SectionNavigation,
  TimeInStageIndicator
};
